import type { Activity, Lead } from '../services/leadsApi'
import {
  type PriorityScore,
  GREEN_SCORE_REASONS,
  RED_SCORE_REASONS,
  YELLOW_SCORE_REASONS,
} from '../constants/leadPriority'

export type { PriorityScore } from '../constants/leadPriority'

/** On by default in this prototype. Set REACT_APP_PHASE2_DEMO=false to use API-only behavior. */
const DEMO_ENABLED = process.env.REACT_APP_PHASE2_DEMO !== 'false'

const PRIORITY_ORDER: Record<PriorityScore, number> = {
  green: 0,
  yellow: 1,
  red: 2,
}

const SCORE_REASONS: Record<PriorityScore, readonly string[]> = {
  green: GREEN_SCORE_REASONS,
  yellow: YELLOW_SCORE_REASONS,
  red: RED_SCORE_REASONS,
}

/** Demo attorney phone (Paul) */
export const DEMO_PAUL_PHONE = '973-920-7921'

export function isPhase2DemoEnabled(): boolean {
  return DEMO_ENABLED
}

function hashIndex(id: string): number {
  let h = 0
  for (let i = 0; i < id.length; i++) {
    h = (h + id.charCodeAt(i)) % 997
  }
  return h
}

export function enrichLead(lead: Lead, listIndex = 0): Lead {
  if (!DEMO_ENABLED) return lead

  const baseIdx = hashIndex(lead.id)
  const idx = baseIdx + listIndex
  const priority_score: PriorityScore =
    idx % 3 === 0 ? 'green' : idx % 3 === 1 ? 'yellow' : 'red'
  const reasons = SCORE_REASONS[priority_score]
  const score_reason = reasons[idx % reasons.length]
  const is_severance = baseIdx % 5 === 0

  return {
    ...lead,
    priority_score,
    score_reason,
    is_severance,
  }
}

export function enrichLeads(leads: Lead[]): Lead[] {
  if (!DEMO_ENABLED) return leads
  const enriched = leads.map((lead, i) => enrichLead(lead, i))
  if (enriched.length === 0) {
    return getSyntheticDemoLeads()
  }
  return enriched
}

function getSyntheticDemoLeads(): Lead[] {
  const now = new Date().toISOString()
  const base: Lead[] = [
    {
      id: 'demo-lead-1',
      first_name: 'Alex',
      last_name: 'Rivera',
      name: 'Alex Rivera',
      phone: '15551234001',
      source: 'website_form',
      status: 'new',
      created_at: now,
      last_comment: {
        id: 'c1',
        comment: 'Sexual harassment reported; no longer employed.',
        commented_on: now,
        commented_by: '',
        commented_by_display: 'System',
      },
    },
    {
      id: 'demo-lead-2',
      first_name: 'Jordan',
      last_name: 'Lee',
      name: 'Jordan Lee',
      phone: '15551234002',
      source: 'calls',
      status: 'assigned',
      created_at: new Date(Date.now() - 86400000).toISOString(),
      assigned_to_attorney: {
        id: 'a2',
        name: 'Paul McKinney',
        email: 'paul@cmlaw.com',
        phone: DEMO_PAUL_PHONE,
      },
      last_comment: {
        id: 'c2',
        comment: 'Intake incomplete — pending OpenAI scoring.',
        commented_on: now,
        commented_by: '',
        commented_by_display: 'Paul',
      },
    },
    {
      id: 'demo-lead-3',
      first_name: 'Morgan',
      last_name: 'Davis',
      name: 'Morgan Davis',
      phone: '15551234003',
      source: 'call_center_api',
      status: 'new',
      created_at: new Date(Date.now() - 172800000).toISOString(),
      last_comment: {
        id: 'c3',
        comment: 'Still employed; alleging retaliation — Camden County.',
        commented_on: now,
        commented_by: '',
        commented_by_display: 'CM LAW',
      },
    },
  ]

  return [
    {
      ...enrichLead(base[0], 0),
      priority_score: 'green',
      score_reason: GREEN_SCORE_REASONS[0],
      is_severance: false,
    },
    {
      ...enrichLead(base[1], 1),
      priority_score: 'yellow',
      score_reason: YELLOW_SCORE_REASONS[1],
      is_severance: true,
    },
    {
      ...enrichLead(base[2], 2),
      priority_score: 'red',
      score_reason: RED_SCORE_REASONS[2],
      is_severance: false,
    },
  ]
}

export function sortLeadsForDemo(leads: Lead[]): Lead[] {
  if (!DEMO_ENABLED) return leads
  return [...leads].sort((a, b) => {
    const pa = PRIORITY_ORDER[a.priority_score ?? 'yellow']
    const pb = PRIORITY_ORDER[b.priority_score ?? 'yellow']
    if (pa !== pb) return pa - pb
    const da = new Date(a.created_at ?? 0).getTime()
    const db = new Date(b.created_at ?? 0).getTime()
    return db - da
  })
}

export function filterLeadsForDemo(
  leads: Lead[],
  options: { search: string; source: string }
): Lead[] {
  if (!DEMO_ENABLED) return leads
  let result = leads

  if (options.source) {
    result = result.filter((l) => l.source === options.source)
  }

  const q = options.search.trim().toLowerCase()
  if (!q) return result

  return result.filter((lead) => {
    const haystack = [
      lead.name,
      lead.phone,
      lead.first_name,
      lead.last_name,
      lead.source,
      lead.last_comment?.comment ?? '',
      lead.score_reason ?? '',
      lead.assigned_to_attorney?.name ?? '',
    ]
      .join(' ')
      .toLowerCase()
    return haystack.includes(q)
  })
}

export function getDemoActivities(leadId: string): Activity[] {
  if (!DEMO_ENABLED) return []

  const idx = hashIndex(leadId)
  const activities: Activity[] = []

  if (idx % 7 === 2) {
    activities.push({
      id: `demo-vm-${leadId}`,
      activity_type: 'VOICEMAIL_RECEIVED',
      title: 'Voicemail transcribed',
      description:
        'Hi, this is Chris — I left a message about my severance offer. Please call me back when you can.',
      timestamp: new Date().toISOString(),
      user: null,
    })
  }

  if (idx % 11 === 3) {
    activities.push({
      id: `demo-req-${leadId}`,
      activity_type: 'ASSIGNMENT_REQUESTED',
      title: 'Assigned per caller request',
      description: 'Caller asked to speak with Paul. Assigned to available attorney (demo).',
      timestamp: new Date().toISOString(),
      user: null,
    })
  }

  return activities
}

export function enrichLeadDetail(lead: Lead): Lead {
  return enrichLead(lead, 0)
}
